import {
  ExtraProduct,
  FullProduct,
  LocalStorageExtraProduct,
  LocalStorageProduct,
} from "ts/api/product";
import { getItem, writeItem } from "tools/localStorage";
import { round, uniqBy } from "lodash";
import {
  calculateFullProductPrice,
  calculateSupplierProductsFeesAmount,
} from "tools/product/productCalculations";
import { PaymentMethodType } from "ts/api/paymentMethod";
import {
  CustomProductOptionConfigType,
  DiscountCodeType,
  IDiscountCode,
  IOrganiserCompany,
  IShow,
} from "@evennt-tech/api-v2-core";
import { OrderProduct, OrderProductDiscount } from "ts/api/order";

import { calculateFullProductDiscount } from "tools/product/productCalculations/calculateFullProductPrice";
import {
  calculatePaymentFeeAmount,
  calculatePlatformFeeAmount,
  calculateProductPriceWithFees,
} from "hooks/helpers";
import { Amounts } from "view/apps/Exhibitor/pages/Cart/types";
import {
  CartDiscountCode,
  discountCodeStore,
} from "store/discount-code/discount-code-store";
import { CartLocalStorageKey } from "view/apps/Exhibitor/pages/Cart/shared";
import { isDiscountCodeFixed } from "tools/discountCode";
import { triggerCartStorageEvt } from "tools/product/cart";
import { roundStoredCartQuantities } from "tools/product/roundCartQuantity";
import { MoneyHelper } from "@evennt-tech/api-v2-payment";

type ApplyDiscountCodesReturn = {
  remainingAmount: number;
  products: FullProduct[];
};

type FixedDiscountAllocation = { base: number; extras: Record<string, number> };

const sortDiscountCode = (a: IDiscountCode, b: IDiscountCode) => {
  const aIsPercentage = a.rules?.[0]?.type === DiscountCodeType.Percentage;
  const bIsPercentage = b.rules?.[0]?.type === DiscountCodeType.Percentage;
  if (aIsPercentage && !bIsPercentage) {
    return -1;
  }
  if (bIsPercentage && !aIsPercentage) {
    return 1;
  }

  return 0;
};

const isDiscountApplicableToProduct = (
  product: FullProduct,
  discountCode: IDiscountCode
) => {
  if (product.owner.id !== discountCode.supplierCompanyId) {
    return false;
  }
  if (!discountCode.productCategories?.length) {
    return true;
  }
  return discountCode.productCategories.some((cat) =>
    product.categories.some(
      (pCat) =>
        pCat.mainCategoryId === cat.productCategoryId ||
        pCat.subCategoryId === cat.productCategoryId
    )
  );
};
const getTotalExtrasAmount = (
  { extras = [] }: FullProduct,
  withFees: boolean
) =>
  extras.reduce(
    (acc, curr) =>
      acc + (withFees ? curr.priceWithFees : curr.price) * (curr.quantity ?? 1),
    0
  );
const getTotalProductsAmountByFixedDiscount = (
  discountCode: IDiscountCode,
  products: FullProduct[],
  withFees: boolean
) => {
  return products.reduce((acc, product) => {
    if (!isDiscountApplicableToProduct(product, discountCode)) {
      return acc;
    }
    const productAmount =
      (withFees ? product.priceWithFees : product.price) *
      (product.cartQuantity ?? 1);
    const extrasAmount = getTotalExtrasAmount(product, withFees);
    return acc + (productAmount + extrasAmount);
  }, 0);
};

const getFixedDiscountsAllocations = (
  appliedDiscountCodes: CartDiscountCode[],
  products: FullProduct[],
  withFees: boolean
) => {
  return appliedDiscountCodes.reduce<
    Record<string, Record<string, FixedDiscountAllocation>>
  >((acc, curr) => {
    if (!isDiscountCodeFixed(curr)) {
      return acc;
    }
    const supplierProduct = products.find(
      (p) => p.owner.id === curr.supplierCompanyId
    );
    if (!supplierProduct) {
      return acc;
    }
    const totalGlobalAmount = getTotalProductsAmountByFixedDiscount(
      curr,
      products,
      withFees
    );
    if (totalGlobalAmount <= 0) {
      return acc;
    }
    const ruleAmount = Number(curr.rules[0].amount);
    const discountAmount = withFees
      ? calculateProductPriceWithFees(
          ruleAmount,
          supplierProduct.organiserFee,
          supplierProduct.ownerToShow.organiserFee ?? 0,
          6
        )
      : ruleAmount;
    const fixedAmountByProduct: Record<string, FixedDiscountAllocation> = {};

    for (const product of products) {
      if (!isDiscountApplicableToProduct(product, curr)) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const productAllocation: FixedDiscountAllocation = {
        base: 0,
        extras: {},
      };

      const baseProductValue =
        (withFees ? product.priceWithFees : product.price) *
        (product.cartQuantity ?? 1);
      const baseProportion = baseProductValue / totalGlobalAmount;
      productAllocation.base = discountAmount * baseProportion;

      product.extras?.forEach((extra) => {
        const extraProductValue =
          (withFees ? extra.priceWithFees : extra.price) * extra.quantity;
        const extraProportion = extraProductValue / totalGlobalAmount;
        productAllocation.extras[extra.id] = discountAmount * extraProportion;
      });

      fixedAmountByProduct[product.id] = productAllocation;
    }
    acc[curr.id] = fixedAmountByProduct;

    return acc;
  }, {});
};

const getTotalAppliedFromDiscountCode = (
  productAmount: number,
  extras: Record<string, number>
): number => {
  const extrasAmount = Object.values(extras).reduce(
    (extraTotal, extraDiscount) => extraTotal + extraDiscount,
    0
  );
  return productAmount + extrasAmount;
};

export function applyDiscountCodes(
  products: FullProduct[],
  appliedDiscountCodes: CartDiscountCode[]
): ApplyDiscountCodesReturn {
  const usedAmountByFixedDiscountCode = appliedDiscountCodes.reduce<
    Record<string, number>
  >((acc, curr) => {
    const rule = curr.rules?.[0];
    if (!acc[curr.id] && rule?.type === DiscountCodeType.Fixed) {
      acc[curr.id] = 0;
    }
    return acc;
  }, {});

  const productDiscountAmountsByDiscountCode = getFixedDiscountsAllocations(
    appliedDiscountCodes,
    products,
    false
  );
  const productDiscountAmountsWithFeesByDiscountCode = getFixedDiscountsAllocations(
    appliedDiscountCodes,
    products,
    true
  );

  const sortedDiscountCodes = [...appliedDiscountCodes].sort(sortDiscountCode);

  const finalProducts = products.map((product) => {
    const qty = product.cartQuantity ?? 1;

    const productBaseAmount = product.price * qty;

    const unitGrossPrice = product.priceWithFees;
    const productBaseAmountWithFees = unitGrossPrice * qty;

    const extrasWithAmounts =
      product.extras?.map<
        ExtraProduct & { amount: number; amountWithFees: number }
      >((extra) => {
        const extraNet = extra.price * extra.quantity;
        const extraUnitGross = extra.priceWithFees;
        return {
          ...extra,
          amount: extraNet,
          amountWithFees: extraUnitGross * extra.quantity,
        };
      }) ?? [];

    const {
      productDiscount,
      productDiscountWithFees,
      discountCodes,
      extrasDiscounts,
      extrasDiscountsWithFees,
    } = sortedDiscountCodes.reduce<{
      productDiscount: number;
      productDiscountWithFees: number;
      discountCodes: OrderProductDiscount[];
      extrasDiscounts: Record<string, number>;
      extrasDiscountsWithFees: Record<string, number>;
    }>(
      (acc, discountCode) => {
        let {
          productDiscount,
          productDiscountWithFees,
          discountCodes,
          extrasDiscounts,
          extrasDiscountsWithFees,
        } = acc;
        const shouldUseDiscount = isDiscountApplicableToProduct(
          product,
          discountCode
        );
        if (!shouldUseDiscount) {
          return acc;
        }

        const rule = discountCode.rules?.[0];
        if (!rule) return acc;

        let productDiscountFromThisCode = 0;
        let productDiscountWithFeesFromThisCode = 0;
        let extraDiscountsFromThisCode: Record<string, number> = {};
        let extraDiscountsWithFeesFromThisCode: Record<string, number> = {};

        const productFixedDiscountAllocation =
          productDiscountAmountsByDiscountCode[discountCode.id]?.[product.id];
        const productFixedDiscountWithFeesAllocation =
          productDiscountAmountsWithFeesByDiscountCode[discountCode.id]?.[
            product.id
          ];

        if (rule.type === DiscountCodeType.Percentage) {
          const percentage = Number(rule.amount) / 100;

          const remainingProductAmount = Math.max(
            productBaseAmount - productDiscount,
            0
          );

          if (remainingProductAmount > 0) {
            const discountForProduct = productBaseAmount * percentage;

            const actualDiscountApplied = Math.min(
              discountForProduct,
              remainingProductAmount
            );
            productDiscount += actualDiscountApplied;
            productDiscountFromThisCode = actualDiscountApplied;
          }

          const remainingProductAmountWithFees = Math.max(
            productBaseAmountWithFees - productDiscountWithFees,
            0
          );
          if (remainingProductAmountWithFees > 0) {
            const discountForProductWithFees =
              productBaseAmountWithFees * percentage;
            const actualDiscountAppliedWithFees = Math.min(
              discountForProductWithFees,
              remainingProductAmountWithFees
            );
            productDiscountWithFees += actualDiscountAppliedWithFees;
            productDiscountWithFeesFromThisCode = actualDiscountAppliedWithFees;
          }

          extrasWithAmounts.forEach((extra) => {
            const appliedExtraDiscount = extrasDiscounts[extra.id] ?? 0;
            const remainingExtraAmount = Math.max(
              extra.amount - appliedExtraDiscount,
              0
            );

            if (remainingExtraAmount === 0) {
              return;
            }
            const discountForThisExtra = extra.amount * percentage;
            const actualAppliedExtraDiscount = Math.min(
              remainingExtraAmount,
              discountForThisExtra
            );

            const newExtraDiscountAmount =
              appliedExtraDiscount + actualAppliedExtraDiscount;
            extrasDiscounts[extra.id] = Math.min(
              newExtraDiscountAmount,
              extra.amount
            );
            extraDiscountsFromThisCode[extra.id] = actualAppliedExtraDiscount;

            const appliedExtraDiscountWithFees =
              extrasDiscountsWithFees[extra.id] ?? 0;
            const remainingExtraAmountWithFees = Math.max(
              extra.amountWithFees - appliedExtraDiscountWithFees,
              0
            );
            if (remainingExtraAmountWithFees > 0) {
              const discountForThisExtraWithFees =
                extra.amountWithFees * percentage;
              const actualAppliedExtraDiscountWithFees = Math.min(
                remainingExtraAmountWithFees,
                discountForThisExtraWithFees
              );

              const newExtraDiscountAmountWithFees =
                appliedExtraDiscountWithFees +
                actualAppliedExtraDiscountWithFees;

              extrasDiscountsWithFees[extra.id] = Math.min(
                newExtraDiscountAmountWithFees,
                extra.amountWithFees
              );
              extraDiscountsWithFeesFromThisCode[
                extra.id
              ] = actualAppliedExtraDiscountWithFees;
            }
          });
        } else if (
          rule.type === DiscountCodeType.Fixed &&
          productFixedDiscountAllocation
        ) {
          const {
            base: baseProductAllocatedAmount,
            extras: extrasAllocatedAmounts,
          } = productFixedDiscountAllocation;
          const {
            base: baseProductAllocatedAmountWithFees,
            extras: extrasAllocatedAmountsWithFees,
          } = productFixedDiscountWithFeesAllocation;

          if (baseProductAllocatedAmount > 0) {
            const remainingProductValue = Math.max(
              0,
              productBaseAmount - productDiscount
            );
            const amountToApplyToProduct = Math.min(
              remainingProductValue,
              baseProductAllocatedAmount
            );
            if (amountToApplyToProduct > 0) {
              productDiscount += amountToApplyToProduct;
              productDiscountFromThisCode = amountToApplyToProduct;
              const remainingProductValueWithFees = Math.max(
                0,
                productBaseAmountWithFees - productDiscountWithFees
              );
              const amountWithFeesToApplyToProduct = Math.min(
                remainingProductValueWithFees,
                baseProductAllocatedAmountWithFees
              );
              productDiscountWithFees += amountWithFeesToApplyToProduct;
              productDiscountWithFeesFromThisCode = amountWithFeesToApplyToProduct;
            }
          }

          extrasWithAmounts.forEach((extra) => {
            const extraAllocatedDiscount = extrasAllocatedAmounts[extra.id];
            if (!extraAllocatedDiscount || extraAllocatedDiscount <= 0) {
              return;
            }
            const extraAllocatedDiscountWithFees =
              extrasAllocatedAmountsWithFees[extra.id] ?? 0;

            const appliedExtraDiscount = extrasDiscounts[extra.id] ?? 0;
            const remainingExtraAmount = Math.max(
              0,
              extra.amount - appliedExtraDiscount
            );

            const amountToApplyToExtra = Math.min(
              extraAllocatedDiscount,
              remainingExtraAmount
            );
            if (amountToApplyToExtra > 0) {
              extrasDiscounts[extra.id] =
                appliedExtraDiscount + amountToApplyToExtra;
              extraDiscountsFromThisCode[extra.id] = amountToApplyToExtra;

              const appliedExtraDiscountWithFees =
                extrasDiscountsWithFees[extra.id] ?? 0;
              const remainingExtraAmountWithFees = Math.max(
                0,
                extra.amountWithFees - appliedExtraDiscountWithFees
              );

              const amountToApplyToExtraWithFees = Math.min(
                remainingExtraAmountWithFees,
                extraAllocatedDiscountWithFees
              );

              const newExtraDiscountAmountWithFees =
                appliedExtraDiscountWithFees + amountToApplyToExtraWithFees;
              extrasDiscountsWithFees[extra.id] = Math.min(
                newExtraDiscountAmountWithFees,
                extra.amountWithFees
              );
              extraDiscountsWithFeesFromThisCode[
                extra.id
              ] = amountToApplyToExtraWithFees;
            }
          });
        }

        const totalAppliedForThisCode = getTotalAppliedFromDiscountCode(
          productDiscountFromThisCode,
          extraDiscountsFromThisCode
        );
        const totalAppliedForThisCodeWithFees = getTotalAppliedFromDiscountCode(
          productDiscountWithFeesFromThisCode,
          extraDiscountsWithFeesFromThisCode
        );

        if (rule.type === DiscountCodeType.Fixed) {
          usedAmountByFixedDiscountCode[
            discountCode.id
          ] += totalAppliedForThisCodeWithFees;
        }

        if (
          totalAppliedForThisCode > 0 ||
          productDiscountWithFeesFromThisCode > 0
        ) {
          discountCodes.push({
            id: discountCode.id,
            expirationId: discountCode.expirations?.[0]?.id as string,
            ruleId: rule.id,
            amount: round(productDiscountFromThisCode, 6),
            amountWithFees: round(productDiscountWithFeesFromThisCode, 6),
            extras: Object.entries(extraDiscountsFromThisCode).map(
              ([extraId, extraDiscount]) => ({
                id: extraId,
                amount: round(extraDiscount, 6),
                amountWithFees: round(
                  extraDiscountsWithFeesFromThisCode[extraId] ?? 0,
                  6
                ),
              })
            ),
          });
        }

        return {
          productDiscount,
          productDiscountWithFees,
          discountCodes,
          extrasDiscounts,
          extrasDiscountsWithFees,
        };
      },
      {
        productDiscount: 0,
        productDiscountWithFees: 0,
        discountCodes: [],
        extrasDiscounts: {},
        extrasDiscountsWithFees: {},
      }
    );

    const newExtras = (product.extras ?? []).map((extra) => {
      const extraDiscount = extrasDiscounts[extra.id];
      const extraDiscountWithFees = extrasDiscountsWithFees[extra.id];

      return {
        ...extra,
        ...(extraDiscount !== undefined && {
          discountAmount: extra.quantity ? extraDiscount / extra.quantity : 0,
        }),
        ...(extraDiscountWithFees !== undefined && {
          discountAmountWithFees: extraDiscountWithFees,
        }),
      };
    });

    const discountAmountPerItem = product.cartQuantity
      ? productDiscount / product.cartQuantity
      : 0;

    return {
      ...product,
      extras: newExtras,
      discountCodes,
      discountAmount: discountAmountPerItem,
      discountAmountWithFees: productDiscountWithFees / qty,
    };
  });

  const remainingAmount = appliedDiscountCodes.reduce<number>(
    (acc, discountCode) => {
      if (discountCode.error) {
        return acc;
      }
      const rule = discountCode.rules?.[0];
      if (rule?.type !== DiscountCodeType.Fixed) {
        return acc;
      }
      const supplierProduct = products.find(
        (p) => p.owner.id === discountCode.supplierCompanyId
      );
      if (!supplierProduct) {
        return acc;
      }

      const appliedAmountWithFees =
        usedAmountByFixedDiscountCode[discountCode.id] ?? 0;
      const discountAmountWithFees = calculateProductPriceWithFees(
        Number(rule.amount),
        supplierProduct.organiserFee,
        supplierProduct.ownerToShow.organiserFee ?? 0
      );
      const remainingAmount = Math.max(
        discountAmountWithFees - appliedAmountWithFees,
        0
      );
      if (remainingAmount === 0) {
        return acc;
      }

      return acc + remainingAmount;
    },
    0
  );

  return {
    products: finalProducts,
    remainingAmount,
  };
}

const clearProductDiscountFields = (product: FullProduct): FullProduct => ({
  ...product,
  discountAmount: 0,
  discountAmountWithFees: 0,
  discountCodes: [],
  extras: (product.extras ?? []).map((extra) => ({
    ...extra,
    discountAmount: 0,
    discountAmountWithFees: 0,
  })),
});

const hasAnyDiscountFields = (product: FullProduct): boolean =>
  !!product.discountAmount ||
  !!product.discountAmountWithFees ||
  !!product.discountCodes?.length ||
  !!product.extras?.some(
    (extra) => !!extra.discountAmount || !!extra.discountAmountWithFees
  );

const getProductWithCalculatedDiscountsAndFees = (
  product: FullProduct,
  organiserCompany: IOrganiserCompany
): FullProduct => {
  const quantity = product.cartQuantity || 1;
  const totalLineDiscount = (product.discountCodes ?? []).reduce(
    (acc, curr) => acc + curr.amount,
    0
  );
  const totalLineDiscountWithFees = (product.discountCodes ?? []).reduce(
    (acc, curr) => acc + curr.amountWithFees,
    0
  );

  const productDiscountAmount = round(totalLineDiscount / quantity, 4);
  const newBasePrice = Math.max(product.price - productDiscountAmount, 0);
  const newPaymentFeeAmount = organiserCompany.paymentFee
    ? calculatePaymentFeeAmount(newBasePrice, organiserCompany.paymentFee)
    : null;
  const newPlatformFeeAmount =
    organiserCompany.fee && newBasePrice
      ? calculatePlatformFeeAmount(newBasePrice, organiserCompany.fee)
      : 0;

  // Fees are recalculated from the discounted extra price. `mapExtras` can't be
  // reused here: it maps api-v2 product-to-extra links, not cart extras.
  const finalExtras = (product.extras ?? []).reduce<ExtraProduct[]>(
    (acc, oldExtra) => {
      const discountAmount = calculateExtraUnitDiscount(
        product,
        oldExtra,
        false
      );
      const discountAmountWithFees = calculateExtraUnitDiscount(
        product,
        oldExtra,
        true
      );
      const newExtraPrice = Math.max(oldExtra.price - discountAmount, 0);

      // Same guards and defaults as the product-level amounts above.
      const extraPaymentFeeAmount = organiserCompany.paymentFee
        ? calculatePaymentFeeAmount(newExtraPrice, organiserCompany.paymentFee)
        : null;
      const extraPlatformFeeAmount =
        organiserCompany.fee && newExtraPrice
          ? calculatePlatformFeeAmount(newExtraPrice, organiserCompany.fee)
          : 0;

      acc.push({
        ...oldExtra,
        discountAmount,
        discountAmountWithFees,
        paymentFeeAmount: extraPaymentFeeAmount,
        platformFeeAmount: extraPlatformFeeAmount,
      });
      return acc;
    },
    []
  );

  const productDiscountAmountWithFees = round(
    totalLineDiscountWithFees / quantity,
    4
  );

  return {
    ...product,
    discountAmountWithFees: productDiscountAmountWithFees,
    discountAmount: productDiscountAmount,
    extras: finalExtras,
    paymentFeeAmount: newPaymentFeeAmount,
    platformFeeAmount: newPlatformFeeAmount,
  };
};

export const applyDiscountCodesToProducts = (
  products: FullProduct[],
  appliedDiscountCodes: CartDiscountCode[],
  organiserCompany: IOrganiserCompany
) => {
  if (!appliedDiscountCodes?.length) {
    discountCodeStore.getState().updateRemainingAmount(0);

    return products.map((product) => {
      if (!hasAnyDiscountFields(product)) {
        return product;
      }

      return getProductWithCalculatedDiscountsAndFees(
        clearProductDiscountFields(product),
        organiserCompany
      );
    });
  }

  const {
    products: productsWithDiscounts,
    remainingAmount,
  } = applyDiscountCodes(products, appliedDiscountCodes);
  discountCodeStore.getState().updateRemainingAmount(remainingAmount);

  return productsWithDiscounts.map((product) => {
    if (!hasAnyDiscountFields(product)) {
      return product;
    }

    return getProductWithCalculatedDiscountsAndFees(product, organiserCompany);
  });
};

export const mergeProducts = (
  showProducts: FullProduct[],
  appliedDiscountCodes: CartDiscountCode[],
  onLocalStorageProductsUpdate: (newProducts: LocalStorageProduct[]) => void,
  organiserCompany: IOrganiserCompany,
  localStorageKey: CartLocalStorageKey,
  canForget?: (productId: string) => boolean
): FullProduct[] => {
  const productsItems = getItem<LocalStorageProduct[]>(localStorageKey, []);
  let hasStorageChanges = false;
  const updatedLocalStorageProducts: LocalStorageProduct[] = [];

  const mergedProducts = productsItems
    .map((storedItem) => {
      const showProduct = showProducts?.find(({ id }) => id === storedItem.id);

      if (!showProduct) {
        if (canForget && !canForget(storedItem.id)) {
          updatedLocalStorageProducts.push(storedItem);
          return null;
        }
        hasStorageChanges = true;
        return null;
      }
      // A quantity that drifted before writes were rounded is healed and
      // stored back, so the shop and the product drawer read it rounded too.
      const productItem = roundStoredCartQuantities(storedItem);
      if (productItem !== storedItem) {
        hasStorageChanges = true;
      }
      const {
        cpoConfigId: cartConfigId = "",
        imageId,
        extras,
        cartQuantity,
      } = productItem;

      const productConfig = showProduct.configs.find(
        ({ id }) => id === cartConfigId
      );
      let shouldUpdateStorageExtras = false;

      const newExtras = showProduct.extras?.length
        ? showProduct.extras.reduce<ExtraProduct[]>((acc, extraProduct) => {
            const storageExtra = extras?.find((e) => e.id === extraProduct.id);

            if (storageExtra) {
              acc.push({
                ...extraProduct,
                quantity: storageExtra.quantity,
              });
            } else if (extraProduct.isSelectedByDefault) {
              shouldUpdateStorageExtras = true;
              acc.push({
                ...extraProduct,
                quantity: cartQuantity ?? 1,
              });
            }
            return acc;
          }, [])
        : [];
      if (shouldUpdateStorageExtras) {
        hasStorageChanges = true;
        const newStorageExtras = newExtras.map<LocalStorageExtraProduct>(
          (extraProduct) => {
            const storageExtra = extras?.find((e) => e.id === extraProduct.id);
            return {
              id: extraProduct.id,
              quantity: extraProduct.quantity,
              ...(storageExtra?.notes && { notes: storageExtra.notes }),
            };
          }
        );

        updatedLocalStorageProducts.push({
          ...productItem,
          extras: newStorageExtras,
        });
      } else {
        updatedLocalStorageProducts.push(productItem);
      }

      let finalProduct = {
        ...showProduct,
        ...productItem,
        extras: newExtras,
        imageId,
      };

      if (productConfig?.type === CustomProductOptionConfigType.CUSTOM) {
        const {
          priceWithFees,
          platformFeeAmount,
          paymentFeeAmount,
          price,
          purchasedQuantity,
        } = productConfig;
        finalProduct = {
          ...finalProduct,
          price: price ?? 0,
          priceWithFees,
          platformFeeAmount,
          paymentFeeAmount,
          purchasedQuantity,
        };
      }

      return finalProduct;
    })
    .filter(Boolean);

  if (hasStorageChanges) {
    writeItem<LocalStorageProduct[]>(
      localStorageKey,
      updatedLocalStorageProducts
    );
    triggerCartStorageEvt(updatedLocalStorageProducts, productsItems);
    onLocalStorageProductsUpdate(updatedLocalStorageProducts);
  }

  return applyDiscountCodesToProducts(
    mergedProducts as FullProduct[],
    appliedDiscountCodes,
    organiserCompany
  );
};

const calculateExtraUnitDiscount = (
  product: FullProduct,
  extra: ExtraProduct,
  includeFees: boolean
) => {
  const totalLineDiscount = (product.discountCodes ?? []).reduce(
    (acc, curr) => {
      const extraDiscount = curr.extras.find((e) => e.id === extra.id);
      if (!extraDiscount) {
        return acc;
      }
      return (
        acc +
        (includeFees ? extraDiscount.amountWithFees : extraDiscount.amount)
      );
    },
    0
  );

  return round(totalLineDiscount / extra.quantity, 4);
};

export const mapProductToLocalStorageProduct = ({
  id,
  cartQuantity,
  notes,
  extras,
  cpoConfigId,
  imageId,
}: FullProduct & {
  cpoConfigId?: string;
  imageId?: string;
}): LocalStorageProduct => ({
  id,
  cartQuantity: cartQuantity || 1,
  notes,
  extras: extras?.map((extraProduct) => ({
    id: extraProduct.id,
    quantity: extraProduct.quantity,
  })),
  ...(cpoConfigId && { cpoConfigId }),
  ...(!!imageId && { imageId }),
});

export const calculateProductPaymentFeeAmount = (
  product: Pick<FullProduct, "paymentFeeAmount" | "cartQuantity" | "extras">
): number => {
  if (!product?.paymentFeeAmount) {
    return 0;
  }
  const productPrice = product.paymentFeeAmount * (product.cartQuantity || 1);
  if (product?.extras?.length) {
    const extrasPaymentFeePrice = product?.extras.reduce(
      (acc, cur) =>
        cur.paymentFeeAmount ? acc + cur.paymentFeeAmount * cur.quantity : acc,
      0
    );
    return productPrice + extrasPaymentFeePrice;
  }
  return productPrice;
};

export const calculateSupplierPaymentFeeAmount = (
  products: Pick<
    FullProduct,
    "fees" | "paymentFeeAmount" | "cartQuantity" | "extras"
  >[]
): number => {
  const fees = uniqBy(products.map((product) => product?.fees).flat(1), "id");
  const feesPaymentFeeAmount = fees.reduce(
    (acc, cur) => acc + (cur.paymentFeeAmount || 0),
    0
  );
  const productsPaymentFeeAmount = products.reduce(
    (sum, product) => sum + calculateProductPaymentFeeAmount(product),
    0
  );
  return feesPaymentFeeAmount + productsPaymentFeeAmount;
};
export const calculateProductPlatformFeeAmount = (
  product: FullProduct
): number => {
  if (!product?.platformFeeAmount) {
    return 0;
  }

  let feeAmount = product.platformFeeAmount * (product.cartQuantity || 1);
  if (product.extras?.length) {
    feeAmount += product.extras.reduce((acc, extra) => {
      if (extra.platformFeeAmount) {
        acc += extra.quantity * extra.platformFeeAmount;
      }
      return acc;
    }, 0);
  }

  return feeAmount;
};
export const calculateSupplierPlatformFeeAmount = (
  supplierProducts: FullProduct[]
) => {
  const uniqueFees = uniqBy(
    supplierProducts.map(({ fees }) => fees).flat(),
    "id"
  );
  const feesPlatformFeeAmount = uniqueFees.reduce(
    (acc, fee) => acc + (fee.platformFeeAmount || 0),
    0
  );
  const productPlatformFeeAmount = supplierProducts.reduce(
    (acc, product) => acc + calculateProductPlatformFeeAmount(product),
    0
  );

  return feesPlatformFeeAmount + productPlatformFeeAmount;
};

export const getDiscountAmount = (
  products: (FullProduct | OrderProduct)[],
  includeFees = true
): number => {
  return products.reduce<number>(
    (acc, curr) => acc + calculateFullProductDiscount(curr, includeFees),
    0
  );
};

export const getFinalTotalAmount = (
  { discountAmount, totalAmount }: Amounts,
  minPaymentAmount: number
): number => {
  if (!discountAmount) {
    return round(totalAmount, 2);
  }

  return Math.max(minPaymentAmount, round(totalAmount, 2));
};

type GetAmountsArgs = {
  products: FullProduct[];
  groupByProperty: string;
  paymentMethodType: PaymentMethodType;
  salesTaxPercentage: number;
};

export const getAmounts = ({
  products,
  salesTaxPercentage,
  paymentMethodType,
}: GetAmountsArgs): Amounts => {
  const feesAmount = calculateSupplierProductsFeesAmount({
    products,
    includeFees: true,
  });

  const productsWithDiscountAmount = products.reduce(
    (sum, product) =>
      sum +
      calculateFullProductPrice({
        product,
        includeFees: true,
        excludeDiscount: true,
      }),
    0
  );
  const discountAmount = getDiscountAmount(products, true);
  const productsAmount = Math.max(
    0,
    productsWithDiscountAmount - discountAmount
  );
  const paymentFeeAmount =
    paymentMethodType === PaymentMethodType.bankTransfer
      ? calculateSupplierPaymentFeeAmount(products)
      : 0;
  const subtotalAmount = feesAmount + productsAmount + paymentFeeAmount;

  const taxAmount = MoneyHelper.to2dp(
    subtotalAmount * (salesTaxPercentage / 100)
  );
  const totalAmount = subtotalAmount + taxAmount;

  return {
    totalAmount,
    paymentFeeAmount: round(paymentFeeAmount, 2),
    subtotalAmount,
    taxAmount,
    discountAmount,
    productsAmount: round(productsWithDiscountAmount, 2),
  };
};
